import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Clock } from "@phosphor-icons/react"
import { Tag, TagVariant } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { Flexibility, Urgency } from "@/types/__generated__/union-types"

interface AppointmentPriorityProps extends React.ComponentProps<"div"> {
  urgency: Urgency
  flexibility: Flexibility
}

const AppointmentPriority = ({ urgency, flexibility, className, ...divProps }: AppointmentPriorityProps) => {
  const { t } = useTranslation()

  const getPriority = useCallback(
    (
      urgency: Urgency,
      flexibility: Flexibility,
    ): { text: string; type: TagVariant; icon?: React.JSX.Element } | null => {
      if (urgency === "MEDICAL_ACUTE") {
        return {
          text: t(`scheduling.waitingRoom.urgency.${urgency}`),
          type: "most-critical",
        }
      }

      if (urgency === "MEDICAL_URGENT") {
        return {
          text: t(`scheduling.waitingRoom.urgency.${urgency}`),
          type: "critical",
        }
      }

      if (flexibility === "FIXED_TIME") {
        return {
          text: t(`scheduling.waitingRoom.flexibility.${flexibility}`),
          type: "warning",
        }
      }

      if (flexibility === "NOT_EARLIER") {
        return {
          text: t(`scheduling.waitingRoom.flexibility.${flexibility}`),
          type: "neutral",
          icon: <Clock size={16} />,
        }
      }
      return null
    },
    [t],
  )

  const priority = useMemo(() => getPriority(urgency, flexibility), [getPriority, urgency, flexibility])

  return (
    <div {...divProps} className={cn("grid grid-cols-2 items-center justify-start gap-1", className)}>
      {priority && <Tag type={priority.type} label={priority.text} icon={priority.icon} />}
    </div>
  )
}
export { AppointmentPriority }
